/**
 * Hyperliquid Builder Fee Adapter
 *
 * Implements PerpsBuilderAdapter. Wraps the one-time EIP-712 builder fee
 * approval flow with localStorage tracking (approval is permanent once
 * submitted to HL, and HL has no API to query approval status).
 */

import {
  getBuilderApprovalTypedData,
  submitBuilderApproval,
} from "../client";
import type { HLBuilderConfig } from "../client";
import type { PerpsSignTypedDataParams } from "../types/trading";
import type { PerpsBuilderAdapter } from "./types";

// ============================================================================
// Storage Helpers
// ============================================================================

const STORAGE_KEY_PREFIX = "hl_builder_approved_";

function getApprovalKey(walletAddress: string, builderAddress: string): string {
  return `${STORAGE_KEY_PREFIX}${walletAddress.toLowerCase()}_${builderAddress.toLowerCase()}`;
}

// ============================================================================
// Adapter Class
// ============================================================================

export class HyperliquidBuilder implements PerpsBuilderAdapter {
  private readonly builder: HLBuilderConfig | null;
  private readonly testnet: boolean;

  constructor(builder: HLBuilderConfig | null, testnet = false) {
    this.builder = builder;
    this.testnet = testnet;
  }

  isApproved(walletAddress: string): boolean {
    if (!this.builder) return true; // No builder configured = no approval needed
    if (typeof window === "undefined") return false;
    return (
      localStorage.getItem(
        getApprovalKey(walletAddress, this.builder.address),
      ) === "true"
    );
  }

  async approve(
    signTypedData: (params: PerpsSignTypedDataParams) => Promise<`0x${string}`>,
    walletAddress: string,
  ): Promise<boolean> {
    if (!this.builder) return true;

    const nonce = Date.now();
    const isMainnet = !this.testnet;
    const typedData = getBuilderApprovalTypedData(
      this.builder,
      nonce,
      isMainnet,
    );

    const signature = await signTypedData({
      domain: typedData.domain,
      types: typedData.types,
      primaryType: typedData.primaryType,
      message: typedData.message as Record<string, unknown>,
    });

    const result = await submitBuilderApproval(
      signature,
      this.builder,
      nonce,
      isMainnet,
    );

    if (result.success) {
      if (typeof window !== "undefined") {
        localStorage.setItem(
          getApprovalKey(walletAddress, this.builder.address),
          "true",
        );
      }
      return true;
    }

    return false;
  }
}
